import React, { useState } from 'react';
import { UnstyledButton } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBan, faCheck, faChevronDown, faChevronUp, faClock, faXmark } from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import type { OrderPrintLogView } from '../../../../../services/orders/orders.service';
import {
  formatLogDateTime,
  getItemsLabel,
  getKindLabel,
  getLogTone,
  getStatusLabel,
  type PrintLogFilter,
  type PrintLogTone,
} from './order-print-log.utils';
import classes from './order-print-log-modal.module.scss';

type OrderPrintLogModalProps = {
  opened: boolean;
  logs: OrderPrintLogView[];
  isLoading: boolean;
  subtitle: string;
  onClose: () => void;
};

const TITLE = 'לוג הדפסות';
const CLOSE_LABEL = 'סגור';
const EMPTY_LABEL = 'אין הדפסות בלוג להזמנה זו';
const EMPTY_FILTER_LABEL = 'אין הדפסות בסינון זה';
const PHONE_QUERY = '(max-width: 767px)';

const FILTERS: { key: PrintLogFilter; label: string }[] = [
  { key: 'all', label: 'הכל' },
  { key: 'printed', label: 'הודפס' },
  { key: 'skipped', label: 'דולג' },
  { key: 'failed', label: 'נכשל' },
];

const TONE_ICON: Record<PrintLogTone, IconProp> = {
  printed: faCheck as IconProp,
  skipped: faBan as IconProp,
  failed: faXmark as IconProp,
  queued: faClock as IconProp,
};

const TONE_CLASS: Record<PrintLogTone, string> = {
  printed: classes.tonePrinted,
  skipped: classes.toneSkipped,
  failed: classes.toneFailed,
  queued: classes.toneQueued,
};

type LogPartProps = { log: OrderPrintLogView; expanded: boolean; onToggle: (id: number) => void };

function StatusBadge({ log, withIcon }: { log: OrderPrintLogView; withIcon: boolean }) {
  const tone = getLogTone(log);
  return (
    <span className={`${classes.badge} ${TONE_CLASS[tone]}`}>
      {getStatusLabel(log)}
      {withIcon ? <FontAwesomeIcon icon={TONE_ICON[tone]} /> : null}
    </span>
  );
}

function ItemsToggle({ log, expanded, onToggle }: LogPartProps) {
  if (log.items.length === 0) {
    const label = log.kind === 'receipt_print' ? 'חשבון' : getItemsLabel(log.lineCount);
    return <span className={classes.itemsLabel}>{label}</span>;
  }
  return (
    <UnstyledButton
      component="button"
      type="button"
      className={classes.itemsToggle}
      onClick={() => onToggle(log.id)}
      aria-expanded={expanded}
      data-testid={`print-log-row-toggle-${log.id}`}
    >
      <span>{getItemsLabel(log.items.length)}</span>
      <FontAwesomeIcon icon={(expanded ? faChevronUp : faChevronDown) as IconProp} className={classes.chevron} />
    </UnstyledButton>
  );
}

function ItemsList({ log }: { log: OrderPrintLogView }) {
  const routeLabels = new Set(log.items.map((item) => item.routeLabel?.trim()).filter(Boolean));
  return (
    <ul className={classes.itemsList} data-testid={`print-log-items-${log.id}`}>
      {log.items.map((item, index) => (
        <li key={`${item.name}-${index}`} className={classes.itemRow}>
          <span>
            {item.name}
            {routeLabels.size > 1 && item.routeLabel ? <span className={classes.itemRouteLabel}> {item.routeLabel}</span> : null}
          </span>
          <span className={classes.itemQty}>×{item.quantity}</span>
        </li>
      ))}
    </ul>
  );
}

function LogCard({ log, expanded, onToggle }: LogPartProps) {
  const { date, time } = formatLogDateTime(log.occurredAt);
  const tone = getLogTone(log);
  return (
    <article className={classes.card} data-testid={`print-log-row-${log.id}`}>
      <div className={classes.cardBody}>
        <span className={`${classes.toneIcon} ${TONE_CLASS[tone]}`}>
          <FontAwesomeIcon icon={TONE_ICON[tone]} />
        </span>
        <div className={classes.cardInfo}>
          <span className={classes.kind}>{getKindLabel(log.kind)}</span>
          <span className={classes.muted}>{date ? `${date} • ${time}` : time}</span>
          <dl className={classes.meta}>
            <dt>עובד</dt>
            <dd>{log.workerName ?? '–'}</dd>
            <dt>מדפסת</dt>
            <dd>{log.printerName ?? '–'}</dd>
          </dl>
        </div>
        <StatusBadge log={log} withIcon={false} />
      </div>
      <div className={classes.cardFooter}>
        <ItemsToggle log={log} expanded={expanded} onToggle={onToggle} />
      </div>
      {expanded ? <ItemsList log={log} /> : null}
    </article>
  );
}

function LogRow({ log, expanded, onToggle }: LogPartProps) {
  const { date, time } = formatLogDateTime(log.occurredAt);
  return (
    <>
      <tr data-testid={`print-log-row-${log.id}`}>
        <td className={classes.timeCell}>
          <span className={classes.kind}>{time}</span>
          <span className={classes.muted}>{date}</span>
        </td>
        <td className={classes.kind}>{getKindLabel(log.kind)}</td>
        <td><StatusBadge log={log} withIcon /></td>
        <td>{log.workerName ?? '–'}</td>
        <td className={classes.ellipsis}>{log.printerName ?? '–'}</td>
        <td><ItemsToggle log={log} expanded={expanded} onToggle={onToggle} /></td>
      </tr>
      {expanded ? (
        <tr className={classes.expandRow}>
          <td colSpan={6}><ItemsList log={log} /></td>
        </tr>
      ) : null}
    </>
  );
}

function PrintLogDialog({ logs, isLoading, subtitle, onClose }: Omit<OrderPrintLogModalProps, 'opened'>) {
  const isPhone = useMediaQuery(PHONE_QUERY, false, { getInitialValueInEffect: false });
  const [filter, setFilter] = useState<PrintLogFilter>('all');
  const [expandedIds, setExpandedIds] = useState<ReadonlySet<number>>(new Set());

  const toggleExpanded = (id: number) => {
    setExpandedIds((previous) => {
      const next = new Set(previous);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  };

  const countOf = (key: PrintLogFilter) => (key === 'all' ? logs.length : logs.filter((log) => getLogTone(log) === key).length);
  const visibleLogs = filter === 'all' ? logs : logs.filter((log) => getLogTone(log) === filter);

  const renderLogs = () => {
    if (isLoading) return <div className={classes.stateLabel}>טוען...</div>;
    if (visibleLogs.length === 0) return <div className={classes.stateLabel}>{filter === 'all' ? EMPTY_LABEL : EMPTY_FILTER_LABEL}</div>;
    if (isPhone) {
      return visibleLogs.map((log) => <LogCard key={log.id} log={log} expanded={expandedIds.has(log.id)} onToggle={toggleExpanded} />);
    }
    return (
      <table className={classes.table}>
        <thead>
          <tr>
            <th>זמן</th>
            <th>סוג</th>
            <th>סטטוס</th>
            <th>עובד</th>
            <th>מדפסת</th>
            <th>פריטים</th>
          </tr>
        </thead>
        <tbody>
          {visibleLogs.map((log) => (
            <LogRow key={log.id} log={log} expanded={expandedIds.has(log.id)} onToggle={toggleExpanded} />
          ))}
        </tbody>
      </table>
    );
  };

  return (
    <div className={classes.overlay} role="dialog" aria-modal="true" aria-label={TITLE} dir="rtl" onClick={onClose}>
      <div className={classes.modal} onClick={(event) => event.stopPropagation()}>
        <header className={classes.header}>
          <div className={classes.titleWrap}>
            <span className={classes.title}>{TITLE}</span>
            <span className={classes.subtitle}>{subtitle}</span>
          </div>
          <UnstyledButton component="button" type="button" className={classes.closeBtn} onClick={onClose} aria-label={CLOSE_LABEL} data-testid="print-log-close">
            ×
          </UnstyledButton>
        </header>

        <div className={classes.filters}>
          {FILTERS.map(({ key, label }) => (
            <UnstyledButton
              key={key}
              component="button"
              type="button"
              className={`${classes.chip} btn-bordered ${filter === key ? classes.chipActive : ''}`}
              onClick={() => setFilter(key)}
              aria-pressed={filter === key}
              data-testid={`print-log-filter-${key}`}
            >
              {`${label} ${countOf(key)}`}
            </UnstyledButton>
          ))}
        </div>

        <div className={classes.content}>{renderLogs()}</div>
      </div>
    </div>
  );
}

const OrderPrintLogModalComponent: React.FC<OrderPrintLogModalProps> = ({ opened, ...dialogProps }) =>
  opened ? <PrintLogDialog {...dialogProps} /> : null;

export const OrderPrintLogModal = React.memo(OrderPrintLogModalComponent);
